<template>
  <div class="min-h-screen lg:flex">
    <Sidebar />
    <GlobalNavbar />
    <main class="min-w-0 flex-1 px-4 pb-8 pt-24 lg:ml-64 lg:px-8">
      <div>
        <div class="flex flex-col gap-6 border-b border-zinc-200 pb-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p class="text-sm font-semibold text-blue-600">Guias práticos</p>
            <h1 class="mt-3 text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">Manuais</h1>
            <p class="mt-4 max-w-2xl text-lg leading-8 text-zinc-600">Aprenda a usar as soluções StackLab com tutoriais objetivos, do primeiro acesso às rotinas mais importantes.</p>
          </div>
        </div>
        <div class="mt-6 flex flex-wrap items-center gap-2">
          <span class="mr-1 text-sm font-medium text-zinc-600">Filtrar por categoria:</span>
          <button v-for="category in manualCategories" :key="category.slug" type="button" :class="selectedCategory === category.slug ? 'border-zinc-950 bg-zinc-950 text-white' : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400'" class="rounded-full border px-3 py-1.5 text-xs font-medium transition" @click="selectedCategory = selectedCategory === category.slug ? '' : category.slug">
            {{ category.name }}
          </button>
        </div>
        <div class="mt-10 space-y-12">
          <section v-for="solution in filteredSolutions" :key="solution.slug">
            <div class="flex items-baseline justify-between gap-4">
              <div>
                <h2 class="text-2xl font-semibold text-zinc-950">{{ solution.name }}</h2>
                <p class="mt-2 text-sm text-zinc-600">{{ solution.description }}</p>
              </div>
              <span class="shrink-0 text-xs font-medium text-zinc-400">{{ solution.manuals.length }} {{ solution.manuals.length === 1 ? 'manual' : 'manuais' }}</span>
            </div>
            <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <Link v-for="manual in solution.manuals" :key="manual.slug" :href="`/manuais/${manual.slug}`" class="group flex min-h-60 flex-col rounded-xl border border-zinc-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-[0_8px_24px_-16px_rgba(24,24,27,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300">
                <div class="flex items-center justify-between gap-3">
                  <span class="inline-flex items-center gap-2 text-xs font-medium text-zinc-500">
                    <span :class="solution.accent === 'blue' ? 'bg-blue-500' : 'bg-emerald-500'" class="h-2 w-2 rounded-full"></span>
                    {{ solution.name }}
                  </span>
                  <span class="rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] font-medium text-zinc-600">{{ categoryName(manual) }}</span>
                </div>
                <h3 class="mt-6 text-lg font-semibold leading-6 text-zinc-950 transition group-hover:text-blue-700">{{ manual.title }}</h3>
                <p class="mt-2 text-sm leading-6 text-zinc-600">{{ manual.description }}</p>
                <span class="mt-auto pt-6 text-sm font-medium text-zinc-700 transition group-hover:text-zinc-950">Ler manual <span aria-hidden="true" class="ml-1 transition group-hover:ml-2">→</span></span>
              </Link>
            </div>
          </section>
          <div v-if="!filteredSolutions.length" class="rounded-xl border border-dashed border-zinc-300 px-6 py-14 text-center">
            <h2 class="text-lg font-semibold text-zinc-900">Nenhum manual encontrado</h2>
            <p class="mt-2 text-sm text-zinc-500">Selecione outra categoria para ver mais manuais.</p>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { Link } from "@inertiajs/vue3";
import { computed, ref } from "vue";
import Sidebar from "../components/layout/Sidebar.vue";
import GlobalNavbar from "../components/layout/GlobalNavbar.vue";
import { getManualCategory, manualCategories, manualCatalog } from "../data/manualCatalog";

const selectedCategory = ref("");
const categoryName = (manual: (typeof manualCatalog)[number]["manuals"][number]) => manualCategories.find((category) => category.slug === getManualCategory(manual))?.name;
const filteredSolutions = computed(() => {
  return manualCatalog
    .map((solution) => ({ ...solution, manuals: solution.manuals.filter((manual) => !selectedCategory.value || getManualCategory(manual) === selectedCategory.value) }))
    .filter((solution) => solution.manuals.length > 0);
});
</script>
